import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import TrustSection from './components/TrustSection';
import AboutSection from './components/AboutSection';
import CategorySection from './components/CategorySection';
import ComparisonSection from './components/ComparisonSection';
import SafetySection from './components/SafetySection';
import ArticleSection from './components/ArticleSection';
import FAQSection from './components/FAQSection';
import { SITE_URL } from '@/constants/site';

const schemaData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'CSSBuy Spreadsheet 2026: Os Melhores Links e Produtos',
  description: 'Encontre o melhor CSSBuy Spreadsheet em português. Links verificados, produtos populares, guias completos e fornecedores confiáveis.',
  url: SITE_URL + '/',
  inLanguage: 'pt-PT',
  dateModified: '2026-06-27',
  publisher: {
    '@type': 'Organization',
    name: 'CSSBuySheet Portugal',
    url: SITE_URL,
  },
  mainEntity: {
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: 'O que é CSSBuy Spreadsheet?', acceptedAnswer: { '@type': 'Answer', text: 'CSSBuy Spreadsheet é uma base de dados completa com links verificados de produtos populares, fornecedores confiáveis e preços atualizados. Criado para ajudar compradores de Portugal e Brasil a encontrar os melhores produtos no CSSBuy de forma rápida e segura, evitando vendedores falsos e golpes.' } },
      { '@type': 'Question', name: 'Como usar o CSSBuy Spreadsheet?', acceptedAnswer: { '@type': 'Answer', text: 'Basta navegar pelas categorias, escolher o produto desejado e clicar no link. Você será redirecionado diretamente para a página do produto na nossa plataforma parceira, onde poderá ver detalhes, preços e fazer a sua compra com total segurança.' } },
      { '@type': 'Question', name: 'CSSBuy é seguro?', acceptedAnswer: { '@type': 'Answer', text: 'Sim, o CSSBuy é uma plataforma segura quando utilizada com os links corretos. O nosso CSSBuy Spreadsheet reúne apenas fornecedores verificados e produtos avaliados pela comunidade, minimizando o risco de fraudes. Recomendamos sempre usar os links do nosso portal.' } },
      { '@type': 'Question', name: 'Quais produtos comprar no CSSBuy?', acceptedAnswer: { '@type': 'Answer', text: 'Os produtos mais populares incluem tênis, hoodies, camisetas, jaquetas, calças, acessórios e perfumes. A nossa planilha categoriza todos os itens por popularidade e avaliação dos compradores, facilitando a sua escolha.' } },
      { '@type': 'Question', name: 'Como encontrar vendedores confiáveis?', acceptedAnswer: { '@type': 'Answer', text: 'No nosso CSSBuy Spreadsheet, todos os vendedores listados passam por uma verificação de reputação. Analisamos histórico de vendas, avaliações de outros compradores e qualidade dos produtos antes de incluir qualquer link na planilha.' } },
      { '@type': 'Question', name: 'Quanto custa o frete do CSSBuy?', acceptedAnswer: { '@type': 'Answer', text: 'O frete varia conforme o destino, peso do produto e método de envio. Para Portugal e Brasil, existem opções que vão desde linhas económicas até envios expressos. A planilha inclui estimativas de frete para cada categoria de produto.' } },
      { '@type': 'Question', name: 'Existe risco de comprar produtos falsos?', acceptedAnswer: { '@type': 'Answer', text: 'Existe sempre algum risco ao comprar online, mas o nosso CSSBuy Spreadsheet reduz significativamente esse risco. Só incluímos links de produtos que foram verificados e testados por compradores reais. Recomendamos sempre verificar as fotos e avaliações antes de finalizar a compra.' } },
      { '@type': 'Question', name: 'Como evitar golpes no CSSBuy?', acceptedAnswer: { '@type': 'Answer', text: 'Use apenas links do nosso CSSBuy Spreadsheet verificado. Evite comprar de vendedores sem histórico. Verifique sempre as avaliações e fotos reais dos produtos. Nunca partilhe dados pessoais ou bancários fora da plataforma oficial.' } },
      { '@type': 'Question', name: 'Qual é o melhor CSSBuy Spreadsheet?', acceptedAnswer: { '@type': 'Answer', text: 'O nosso CSSBuy Spreadsheet é reconhecido como o melhor portal em português, com mais de 50.000 produtos verificados, atualização diária e suporte dedicado para compradores de Portugal e Brasil. Links seguros e curadoria profissional.' } },
      { '@type': 'Question', name: 'Como economizar nas compras do CSSBuy?', acceptedAnswer: { '@type': 'Answer', text: 'Utilize o nosso CSSBuy Spreadsheet para comparar preços entre diferentes fornecedores. A planilha destaca as melhores ofertas e produtos com melhor relação qualidade-preço. Além disso, agrupar vários itens no mesmo envio reduz significativamente o custo do frete.' } },
    ],
  },
};

export default function Home() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <Header />
      <main>
        <HeroSection />
        <TrustSection />
        <article>
          <AboutSection />
        </article>
        <CategorySection />
        <ComparisonSection />
        <SafetySection />
        <ArticleSection />
        <FAQSection />
      </main>
      <section className="bg-accent-100 py-12 md:py-16">
        <div className="w-full px-4 md:px-6 text-center">
          <h2 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
            Pronto para encontrar os melhores produtos?
          </h2>
          <p className="mt-3 text-sm text-foreground-600 max-w-md mx-auto">
            Explore o CSSBuy Spreadsheet agora e descubra milhares de produtos verificados.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-7 py-3.5 rounded-full text-sm font-bold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              CSSBuy Sheet
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-sm"></i>
              </span>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 border-2 border-primary-500 text-primary-600 px-7 py-3.5 rounded-full text-sm font-bold hover:bg-primary-500 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-shopping-bag-line text-sm"></i>
              </span>
            </a>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}